/**
 * DriftBanner — Story SCH-5.3.
 *
 * Replaces the generic "fields may have changed" Alert previously inline in
 * FieldMappingTab. Computes the diff client-side from data the
 * useDiscoverCatalog() query already has — schFields (current SCH state) +
 * existingMappings (last-confirmed Joules state). No backend round-trip
 * for the diff detail.
 *
 * AC3 — three sections behind a "View changes" toggle:
 *   - Added in SubContractorHub: new fields not yet mapped.
 *   - No longer in SubContractorHub: mappings whose externalFieldKey is gone.
 *   - Unchanged: collapsed expander (cosmetic).
 *
 * AC5 — REMOVED rows offer "Remove mapping" via useDeleteFieldMapping.
 *
 * Defer for V1: AC4 "Map now" scroll-to-row affordance. The diff list +
 * existing Field Mapping sections below cover the workflow; admins can
 * still confirm new mappings via the regular Field Mapping UI. Will revisit
 * if support data shows admins want the direct jump.
 */

import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type {
  FieldMappingRow,
  SchCustomField,
} from '@/types/integrations/fieldMapping';

interface DriftBannerProps {
  schFields: SchCustomField[];
  existingMappings: FieldMappingRow[];
  /** Fires when admin clicks "Remove mapping" on a no-longer-in-SCH row. */
  onRemoveMapping: (mappingId: string) => void;
  /** Mapping id currently being deleted (drives per-row spinner). */
  deletingMappingId?: string;
}

interface DiffBuckets {
  added: SchCustomField[];
  removed: FieldMappingRow[];
  unchanged: FieldMappingRow[];
}

function computeDiff(
  schFields: SchCustomField[],
  existingMappings: FieldMappingRow[],
): DiffBuckets {
  const mappedKeys = new Set(existingMappings.map((m) => m.externalFieldKey));
  const currentKeys = new Set(schFields.map((f) => f.fieldKey));

  const added = schFields.filter((f) => !mappedKeys.has(f.fieldKey));
  const removed = existingMappings.filter(
    (m) => !currentKeys.has(m.externalFieldKey),
  );
  const unchanged = existingMappings.filter((m) =>
    currentKeys.has(m.externalFieldKey),
  );
  return { added, removed, unchanged };
}

export function DriftBanner({
  schFields,
  existingMappings,
  onRemoveMapping,
  deletingMappingId,
}: DriftBannerProps) {
  const [expanded, setExpanded] = useState(false);
  const [showUnchanged, setShowUnchanged] = useState(false);

  const { added, removed, unchanged } = computeDiff(schFields, existingMappings);

  // If the only thing that's "drifted" is some auto-match noise (no actual
  // adds or removes), don't shout — silent re-discovery handles it.
  if (added.length === 0 && removed.length === 0) {
    return null;
  }

  return (
    <Alert variant="default" className="border-amber-300 bg-amber-50 dark:bg-amber-950/20">
      <AlertTriangle className="h-4 w-4 text-amber-600" />
      <AlertTitle>SubContractorHub schema has changed since last setup</AlertTitle>
      <AlertDescription className="space-y-2">
        <div className="text-sm">
          {added.length > 0 && (
            <span className="mr-3">
              <strong>{added.length}</strong> new field{added.length === 1 ? '' : 's'} in SubContractorHub
            </span>
          )}
          {removed.length > 0 && (
            <span>
              <strong>{removed.length}</strong> mapped field{removed.length === 1 ? '' : 's'} no longer in SubContractorHub
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? (
            <ChevronDown className="mr-1 h-3 w-3" />
          ) : (
            <ChevronRight className="mr-1 h-3 w-3" />
          )}
          {expanded ? 'Hide changes' : 'View changes'}
        </Button>

        {expanded && (
          <div className="mt-3 space-y-3">
            {added.length > 0 && (
              <DiffSection
                title={`Added in SubContractorHub (${added.length})`}
                description="New custom fields you haven't mapped yet. Use the Field Mapping sections below to confirm matches or map manually."
              >
                {added.map((f) => (
                  <li
                    key={f.fieldKey}
                    className="flex items-center justify-between py-1 text-sm"
                  >
                    <span className="truncate">
                      <span className="font-medium">{f.fieldLabel}</span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        ({f.fieldKey})
                      </span>
                    </span>
                    <Badge variant="outline" className="text-[10px] py-0">
                      {f.fieldType}
                    </Badge>
                  </li>
                ))}
              </DiffSection>
            )}

            {removed.length > 0 && (
              <DiffSection
                title={`No longer in SubContractorHub (${removed.length})`}
                description="Mappings that reference fields SubContractorHub no longer exposes. They won't carry any data on future syncs."
              >
                {removed.map((m) => {
                  const isDeleting = deletingMappingId === m.id;
                  return (
                    <li
                      key={m.id}
                      className="flex items-center justify-between py-1 text-sm"
                    >
                      <span className="truncate">
                        <span className="font-medium">{m.externalFieldLabel}</span>
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({m.externalFieldKey})
                        </span>
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-destructive hover:text-destructive"
                        disabled={isDeleting}
                        onClick={() => onRemoveMapping(m.id)}
                      >
                        {isDeleting ? (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        ) : null}
                        Remove mapping
                      </Button>
                    </li>
                  );
                })}
              </DiffSection>
            )}

            {unchanged.length > 0 && (
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-muted-foreground"
                  onClick={() => setShowUnchanged((v) => !v)}
                >
                  {showUnchanged ? (
                    <ChevronDown className="mr-1 h-3 w-3" />
                  ) : (
                    <ChevronRight className="mr-1 h-3 w-3" />
                  )}
                  {showUnchanged
                    ? `Hide ${unchanged.length} unchanged`
                    : `Show ${unchanged.length} unchanged`}
                </Button>
                {showUnchanged && (
                  <DiffSection
                    title={null}
                    description="Mappings still present in SubContractorHub — no action needed."
                  >
                    {unchanged.map((m) => (
                      <li key={m.id} className="py-1 text-sm">
                        <span className="font-medium">{m.externalFieldLabel}</span>
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({m.externalFieldKey})
                        </span>
                      </li>
                    ))}
                  </DiffSection>
                )}
              </div>
            )}
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}

interface DiffSectionProps {
  title: string | null;
  description?: string;
  children: React.ReactNode;
}

function DiffSection({ title, description, children }: DiffSectionProps) {
  return (
    <div className="rounded-md border bg-background/60 p-3">
      {title && <h4 className="text-xs font-semibold uppercase tracking-wide">{title}</h4>}
      {description && (
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      )}
      <ul className="mt-2 divide-y">{children}</ul>
    </div>
  );
}
